import React, {
  useCallback, useContext, useState,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Truncate from 'react-truncate';
import IconButton from '@material-ui/core/IconButton';
import { SearchFieldContext } from 'components/SearchFieldContext';
import UserAvatar from 'components/UserAvatar';
import ChatroomIcon from 'components/icons/Chatroom';
import TextHighlighter from 'components/TextHighlighter';
import {
  UserTag, UserInfo,
  UserDescription,
} from 'components/chat/ChatUserComponents';
import useTwilioEventFunctions from 'components/twilio/hooks/useTwilioEventFunctions';

const RootContainer = styled.div`
  padding: 10px 16px;
  display: flex;
  align-items: center;
  background-color: #FFF;
  border-radius: 4px;
  height:
    ${({ variant }) => {
    switch (variant) {
      case 'compact':
        return '54px';
      case 'standard':
      default:
        return '90px';
    }
  }};
  margin-bottom:
    ${({ variant }) => {
    switch (variant) {
      case 'compact':
        return '0px';
      case 'standard':
      default:
        return '10px';
    }
  }};
`;

const UserInfoContainer = styled.div`
  flex: 1 1 auto;
  margin-left: 10px;
  width: 60%;
`;

const StyledChatroomIcon = styled(ChatroomIcon)`
  cursor: pointer;
`;

const ParticipantSearchResultItem = ({
  uuid,
  name,
  userTag,
  userDescription,
  variant,
}) => {
  const { searchKeyword } = useContext(SearchFieldContext);
  const [isLoading, setLoading] = useState(false);
  const { createPrivateChat } = useTwilioEventFunctions();

  const onPrivateChatBtnClick = useCallback(async (e) => {
    e.stopPropagation();
    setLoading(true);
    await createPrivateChat(uuid);
    setLoading(false);
    window.trackingEventToFirebase(null, 'Chat', 'OpenPrivateChatWindow', 'SearchField');
  }, [createPrivateChat, uuid]);

  return (
    <RootContainer variant={variant}>
      <UserAvatar name={name} />
      <UserInfoContainer>
        <UserInfo>
          <TextHighlighter
            searchWords={[searchKeyword]}
            textToHighlight={name}
          />
          {
            userTag && (
              <UserTag>
                {userTag}
              </UserTag>
            )
          }
        </UserInfo>
        {
          (userDescription || []).length > 0 && (
            <UserDescription>
              <Truncate lines={2} ellipsis="...">
                {userDescription}
              </Truncate>
            </UserDescription>
          )
        }
      </UserInfoContainer>
      <IconButton onClick={onPrivateChatBtnClick} disabled={!uuid || isLoading}>
        <StyledChatroomIcon
          color="#484848"
          width="24px"
          height="24px"
          onClick={onPrivateChatBtnClick}
        />
      </IconButton>
    </RootContainer>
  );
};

ParticipantSearchResultItem.propTypes = {
  uuid: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  userTag: PropTypes.string,
  userDescription: PropTypes.string,
  variant: PropTypes.oneOf([
    'compact',
    'standard',
  ]),
};

ParticipantSearchResultItem.defaultProps = {
  userTag: '',
  userDescription: '',
  variant: 'standard',
};

export default ParticipantSearchResultItem;
